import { apiClient, unwrap } from './client';

/**
 * SIMG-PATRIMOINE-2026-001 — registre des immobilisations.
 *
 * Les montants circulent en centimes de FCFA, comme partout ailleurs dans la
 * plateforme, et arrivent en chaîne de caractères parce que ce sont des BigInt
 * côté serveur.
 */

export type CategorieImmobilisation =
  | 'materiel_outillage'
  | 'materiel_transport'
  | 'materiel_mobilier'
  | 'materiel_informatique'
  | 'agencements'
  | 'autre';

export type StatutImmobilisation =
  | 'en_service'
  | 'en_reparation'
  | 'hors_service'
  | 'cede'
  | 'rebute';

export interface CategorieReferentiel {
  code:                 CategorieImmobilisation;
  libelle:              string;
  compteImmobilisation: string;
  compteAmortissement:  string;
  dureeMoisParDefaut:   number;
  dureeAnsParDefaut:    number;
}

export interface Immobilisation {
  id:                       string;
  codeInventaire:           string;
  designation:              string;
  categorie:                CategorieImmobilisation;
  description:              string | null;
  marque:                   string | null;
  modele:                   string | null;
  numeroSerie:              string | null;
  compteImmobilisationCode: string;
  compteAmortissementCode:  string;
  dateAcquisition:          string;
  valeurAcquisitionCents:   string;
  valeurResiduelleCents:    string;
  cumulAmortissementCents:  string;
  valeurNetteCents:         string;
  dureeAmortissementMois:   number;
  dateMiseEnService:        string | null;
  statut:                   StatutImmobilisation;
  localisation:             string | null;
  detenteurNom:             string | null;
  dateSortie:               string | null;
  motifSortie:              string | null;
  valeurCessionCents:       string | null;
  fournisseur?:             { id: string; raisonSociale: string } | null;
  amortissements?:          Amortissement[];
  mouvements?:              MouvementImmobilisation[];
}

export interface Amortissement {
  id:             string;
  periode:        string;
  montantCents:   string;
  cumulCents:     string;
  comptabiliseLe: string;
}

export interface MouvementImmobilisation {
  id:                string;
  type:              string;
  date:              string;
  localisationAvant: string | null;
  localisationApres: string | null;
  detenteurAvant:    string | null;
  detenteurApres:    string | null;
  motif:             string | null;
}

export interface EcheanceAmortissement {
  periode:          string;
  montantCents:     string;
  cumulCents:       string;
  valeurNetteCents: string;
  comptabilise:     boolean;
}

export interface SynthesePatrimoine {
  nombreBiens:         number;
  valeurBruteCents:    string;
  amortissementsCents: string;
  valeurNetteCents:    string;
  categories: {
    categorie:           CategorieImmobilisation;
    libelle:             string;
    nombre:              number;
    valeurBruteCents:    string;
    amortissementsCents: string;
    valeurNetteCents:    string;
  }[];
}

export interface CreerImmobilisationInput {
  /** Lot 2, étape 2.10 : détenu avant 2026 (reprise) ou acheté en 2026 hors bon de commande (écriture à la saisie). */
  origine:                  'reprise' | 'achat_hors_bca';
  reglement?:               'fournisseur' | 'banque' | 'caisse';
  designation:              string;
  categorie:                CategorieImmobilisation;
  valeurAcquisitionCents:   number;
  dateAcquisition:          string;
  dureeAmortissementMois?:  number;
  cumulAmortissementCents?: number;
  valeurResiduelleCents?:   number;
  dateMiseEnService?:       string;
  description?:             string;
  marque?:                  string;
  modele?:                  string;
  numeroSerie?:             string;
  localisation?:            string;
  detenteurNom?:            string;
  motifEntree?:             string;
}

export const patrimoineApi = {
  categories: () =>
    apiClient.get('/patrimoine/categories').then(unwrap<CategorieReferentiel[]>),

  synthese: () =>
    apiClient.get('/patrimoine/synthese').then(unwrap<SynthesePatrimoine>),

  lister: (params?: {
    statut?: StatutImmobilisation;
    categorie?: CategorieImmobilisation;
    recherche?: string;
    inclureSorties?: boolean;
    page?: number;
    limit?: number;
  }) =>
    apiClient
      .get('/patrimoine/immobilisations', { params })
      .then(unwrap<{ data: Immobilisation[]; pagination: { page: number; limit: number; total: number; pages: number } }>),

  detail: (id: string) =>
    apiClient.get(`/patrimoine/immobilisations/${id}`).then(unwrap<Immobilisation>),

  creer: (body: CreerImmobilisationInput) =>
    apiClient.post('/patrimoine/immobilisations', body).then(unwrap<Immobilisation>),

  affecter: (id: string, body: { localisation?: string; detenteurNom?: string; statut?: StatutImmobilisation; motif?: string }) =>
    apiClient.patch(`/patrimoine/immobilisations/${id}/affecter`, body).then(unwrap<Immobilisation>),

  sortir: (id: string, body: { type: 'cession' | 'rebut'; motif: string; valeurCessionCents?: number; dateSortie?: string }) =>
    apiClient.post(`/patrimoine/immobilisations/${id}/sortir`, body).then(unwrap<Immobilisation>),

  planAmortissement: (id: string) =>
    apiClient.get(`/patrimoine/immobilisations/${id}/plan-amortissement`).then(unwrap<EcheanceAmortissement[]>),

  doter: (periode: string) =>
    apiClient
      .post('/patrimoine/amortissements/doter', { periode })
      .then(unwrap<{ nombreBiens: number; montantTotalCents: string; journalEntryId: string | null; message: string }>),
};
